import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { isWaitingForOcrQuota } from '@/user_files/utils/processing-status'
import { getUserFilesUploadFilenameGuidance } from '@/user_files/utils/upload-validation'

export type UserFileAttachmentStatus = 'uploading' | 'processing' | 'ready' | 'error'

export type UserFileAttachmentStatusResult = {
  status: UserFileAttachmentStatus
  errorMessage?: string
}

export function getUserFileAttachmentStatus(params: {
  file?: UserFileRead
  uploadState?: FileUploadState
  loadError?: boolean
}): UserFileAttachmentStatusResult {
  const { file, uploadState, loadError } = params
  const client = uploadState?.client
  const clientStatus = client?.uploadStatus

  if (client && clientStatus === 'unsupported_type') {
    return {
      status: 'error',
      errorMessage: getUserFilesUploadFilenameGuidance(client.filename) ?? 'Unsupported file type',
    }
  }

  if (clientStatus === 'error') {
    return { status: 'error', errorMessage: uploadState?.client.uploadError ?? 'Upload failed' }
  }

  if (clientStatus === 'validating' || clientStatus === 'queued' || clientStatus === 'initializing' || clientStatus === 'uploading') {
    return { status: 'uploading' }
  }

  const record = uploadState?.backend?.record ?? file

  if (clientStatus === 'uploaded' && !record) {
    return { status: 'processing' }
  }

  if (loadError && !record) {
    return { status: 'error', errorMessage: 'Could not load file' }
  }

  if (!record) {
    return { status: 'processing' }
  }

  if (record?.processing_status === UserFileProcessingStatus.Failed) {
    return { status: 'error', errorMessage: record.processing_error ?? 'Processing failed' }
  }

  if (isWaitingForOcrQuota(record)) {
    return { status: 'error', errorMessage: 'Waiting for OCR page quota. Remove this file from chat to continue.' }
  }

  if (record?.processing_status === UserFileProcessingStatus.Pending || record?.processing_status === UserFileProcessingStatus.Processing) {
    return { status: 'processing' }
  }

  return { status: 'ready' }
}
